const LOGIN_AS_HASH_QUERY = /[?&]hash=/;

const isUrlWithHashParam = (value) =>
  typeof value === "string" &&
  LOGIN_AS_HASH_QUERY.test(value) &&
  (/^https?:\/\//i.test(value) ||
    (value.startsWith("/") && !value.startsWith("//")));

// Drops only the one-time owner login-as `hash` query param. Other params,
// the fragment, and non-URL strings stay as they are. URLSearchParams deletes
// every `hash` key, including repeats, without a second pass over `?&` / `&&`.
export const stripLoginAsHashParam = (value) => {
  if (!isUrlWithHashParam(value)) return value;
  try {
    const isAbsolute = /^https?:\/\//i.test(value);
    const url = new URL(value, "https://placeholder.local");
    if (!url.searchParams.has("hash")) return value;
    url.searchParams.delete("hash");
    if (!isAbsolute) return `${url.pathname}${url.search}${url.hash}`;
    return url.toString();
  } catch {
    return value;
  }
};

const sanitizeUrlProperties = (value, seen = new WeakSet()) => {
  if (typeof value === "string") return stripLoginAsHashParam(value);
  if (!value || typeof value !== "object") return value;
  if (seen.has(value)) return value;
  seen.add(value);

  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item) => {
      const sanitized = sanitizeUrlProperties(item, seen);
      if (sanitized !== item) changed = true;
      return sanitized;
    });
    return changed ? next : value;
  }

  let changed = false;
  const next = {};
  Object.keys(value).forEach((key) => {
    const sanitized = sanitizeUrlProperties(value[key], seen);
    if (sanitized !== value[key]) changed = true;
    next[key] = sanitized;
  });
  return changed ? next : value;
};

// /flags person_properties are read from persistence, not from the event
// pipeline. $initial_current_url and $initial_referrer come from these keys.
const PERSISTED_POSTHOG_URL_KEYS = [
  "$initial_person_info",
  "$initial_referrer_info",
  "$initial_campaign_params",
  "$client_session_props",
  "$stored_person_properties",
];

const valueMayContainLoginAsHash = (value) => {
  if (value == null) return false;
  if (typeof value === "string") return LOGIN_AS_HASH_QUERY.test(value);
  try {
    return LOGIN_AS_HASH_QUERY.test(JSON.stringify(value));
  } catch {
    return false;
  }
};

const persistedKeysMayContainLoginAsHash = (posthog) =>
  PERSISTED_POSTHOG_URL_KEYS.some((key) =>
    valueMayContainLoginAsHash(posthog.get_property?.(key))
  );

// Rewrites the stored login-as URL before /flags (and the next capture) reads
// it. before_send alone never sees the flags body. Safe to call from `loaded`
// (runs before the first /flags) and from before_send (runs after
// set_initial_person_info and before identify reloads flags).
export const stripLoginAsHashFromPostHogPersistence = (posthog) => {
  if (
    typeof posthog?.get_property !== "function" ||
    typeof posthog?.register !== "function"
  ) {
    return;
  }
  try {
    const updates = {};
    PERSISTED_POSTHOG_URL_KEYS.forEach((key) => {
      const current = posthog.get_property(key);
      const next = sanitizeUrlProperties(current);
      if (next !== current) updates[key] = next;
    });
    if (Object.keys(updates).length > 0) posthog.register(updates);
  } catch {
    // Persistence shape varies. Never throw from init or before_send.
  }
};

export const stripLoginAsHashFromPostHogEvent = (event) => {
  try {
    if (!event || typeof event !== "object") return event;
    const properties = sanitizeUrlProperties(event.properties);
    const set = sanitizeUrlProperties(event.$set);
    const setOnce = sanitizeUrlProperties(event.$set_once);
    if (
      properties === event.properties &&
      set === event.$set &&
      setOnce === event.$set_once
    ) {
      return event;
    }
    const next = { ...event };
    if (properties !== event.properties) next.properties = properties;
    if (set !== event.$set) next.$set = set;
    if (setOnce !== event.$set_once) next.$set_once = setOnce;
    return next;
  } catch {
    return event;
  }
};

// posthog-js before_send. Removes `hash` from URL-valued properties and from
// persisted props that /flags sends as person_properties. Skips work when
// nothing in persistence or the outgoing event still carries a login-as hash.
export const stripLoginAsHashBeforeSend = (posthog) => (event) => {
  if (persistedKeysMayContainLoginAsHash(posthog)) {
    stripLoginAsHashFromPostHogPersistence(posthog);
  }
  if (valueMayContainLoginAsHash(event)) {
    return stripLoginAsHashFromPostHogEvent(event);
  }
  return event;
};
